import { Nav } from "../components/Nav";
import { Logo } from "../components/Logo";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, ShieldCheck } from "lucide-react";
import { PrintPdfButton } from "../components/PrintPdfButton";
import { VersionHistory } from "../components/VersionHistory";
import { LegalLanguageSwitcher } from "../components/LegalLanguageSwitcher";

const SECTIONS = [
    {
        n: 1,
        title: "Information We Collect",
        body: "We may collect information that you provide directly, including:",
        bullets: [
            "Name",
            "Email address",
            "Account information",
            "Business or organization details",
            "Contact information",
            "Links and QR codes you create",
            "Communications with our support team",
        ],
        after: "We may also automatically collect certain technical information, including:",
        afterBullets: [
            "IP address",
            "Browser type",
            "Device information",
            "Operating system",
            "Date and time of access",
            "Referring website",
            "General geographic location",
            "Link click and QR code scan statistics",
        ],
    },
    {
        n: 2,
        title: "How We Use Your Information",
        body: "We use your information to:",
        bullets: [
            "Provide and maintain our services",
            "Create and manage shortened URLs and QR codes",
            "Authenticate user accounts",
            "Improve our products and services",
            "Analyze service performance and usage",
            "Detect fraud, abuse, and security threats",
            "Respond to customer inquiries",
            "Comply with legal obligations",
            "Communicate important service updates",
        ],
    },
    {
        n: 3,
        title: "Analytics",
        body: "SpLink.pro may collect anonymous or aggregated analytics related to shortened links and QR code usage, such as the number of clicks, scan counts, approximate location, device type, browser, and referral source. These analytics help us improve our services and provide insights to account holders.",
    },
    {
        n: 4,
        title: "Sharing Information",
        callout: "We do not sell your personal information.",
        body: "We may share information with:",
        bullets: [
            "Trusted service providers who help operate our services",
            "Law enforcement or government authorities when required by law",
            "Successors in the event of a merger, acquisition, or business transfer",
            "Other parties with your consent",
        ],
    },
    {
        n: 5,
        title: "Data Security",
        body: "We use reasonable administrative, technical, and organizational safeguards to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    },
    {
        n: 6,
        title: "Data Retention",
        body: "We retain information only as long as necessary to provide our services, comply with legal obligations, resolve disputes, enforce our agreements, or protect the security and integrity of our platform.",
    },
    {
        n: 7,
        title: "Your Choices",
        body: "Depending on your location and applicable laws, you may have the right to:",
        bullets: [
            "Access your personal information",
            "Correct inaccurate information",
            "Request deletion of your account or personal data",
            "Object to or restrict certain processing",
            "Withdraw consent where applicable",
        ],
        after: "To exercise these rights, please contact us using the information below.",
    },
    {
        n: 8,
        title: "Third-Party Websites",
        body: "Shortened links may direct users to third-party websites. We are not responsible for the privacy practices, content, or security of those external sites. We encourage you to review their privacy policies before providing personal information.",
    },
    {
        n: 9,
        title: "Children's Privacy",
        body: "SpLink.pro is not intended for children under the age required by applicable law, and we do not knowingly collect personal information from children.",
    },
    {
        n: 10,
        title: "Changes to This Privacy Policy",
        body: 'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. Continued use of our services after changes become effective constitutes acceptance of the revised policy.',
    },
];

export default function Privacy() {
    return (
        <div className="App min-h-screen">
            <Nav />
            <LegalLanguageSwitcher doc="privacy">

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" aria-hidden />
                <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[hsl(100,76%,42%)]/10 blur-3xl" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-6 pb-14 pt-24">
                    <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="privacy-back-link">
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium uppercase tracking-widest text-muted-foreground" data-testid="privacy-badge">
                        <ShieldCheck className="h-3 w-3 text-[hsl(100,76%,42%)]" />
                        Legal
                    </div>
                    <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl" data-testid="privacy-heading">
                        SpLink.pro™<br />
                        <span className="brand-gradient-text">Privacy Policy</span>
                    </h1>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div className="font-mono text-xs text-muted-foreground">
                            <span className="text-foreground/60">Effective date:</span>{" "}
                            <span className="ml-1">August 4, 2026</span>
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                            <span className="text-foreground/60">Last updated:</span>{" "}
                            <span className="ml-1">August 5, 2026</span>
                        </div>
                        <PrintPdfButton testId="privacy-print-btn" />
                    </div>
                    <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground">
                        <strong className="text-foreground">SpLink.pro™</strong> ("SpLink.pro," "we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use SpLink.pro, a URL shortening, QR code, and link management service that is part of the SocialPulse ecosystem.
                    </p>
                    <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
                        By using our services, you agree to the practices described in this Privacy Policy.
                    </p>
                </div>
            </section>

            {/* TOC + content */}
            <section className="border-b border-border">
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-16 lg:grid-cols-[240px_1fr]">
                    <aside className="hidden lg:block">
                        <div className="sticky top-24">
                            <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Contents</div>
                            <nav className="flex flex-col gap-1.5 font-mono text-xs">
                                {SECTIONS.map((s) => (
                                    <a
                                        key={s.n}
                                        href={`#section-${s.n}`}
                                        className="text-muted-foreground hover:text-foreground"
                                        data-testid={`toc-link-${s.n}`}
                                    >
                                        <span className="text-[hsl(100,76%,42%)]">{String(s.n).padStart(2, "0")}</span>{" "}
                                        {s.title}
                                    </a>
                                ))}
                                <a href="#section-11" className="text-muted-foreground hover:text-foreground" data-testid="toc-link-11">
                                    <span className="text-[hsl(100,76%,42%)]">11</span> Contact Us
                                </a>
                            </nav>
                        </div>
                    </aside>

                    <article className="max-w-3xl">
                        {SECTIONS.map((s) => (
                            <section
                                key={s.n}
                                id={`section-${s.n}`}
                                className="border-b border-border py-8 first:pt-0"
                                data-testid={`privacy-section-${s.n}`}
                            >
                                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                                    Section {String(s.n).padStart(2, "0")}
                                </div>
                                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                                    {s.title}
                                </h2>
                                {s.callout && (
                                    <p className="mt-4 rounded-md border-l-4 border-[hsl(100,76%,42%)] bg-card p-4 font-display text-lg font-semibold text-foreground">
                                        {s.callout}
                                    </p>
                                )}
                                {s.body && (
                                    <p className="mt-4 text-base leading-relaxed text-muted-foreground">{s.body}</p>
                                )}
                                {s.bullets && (
                                    <ul className="mt-4 space-y-2">
                                        {s.bullets.map((b) => (
                                            <li key={b} className="flex gap-3 text-base text-muted-foreground">
                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(217,91%,45%)]" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {s.after && (
                                    <p className="mt-4 text-base leading-relaxed text-muted-foreground">{s.after}</p>
                                )}
                                {s.afterBullets && (
                                    <ul className="mt-4 space-y-2">
                                        {s.afterBullets.map((b) => (
                                            <li key={b} className="flex gap-3 text-base text-muted-foreground">
                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(100,76%,42%)]" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        ))}

                        {/* Contact */}
                        <section id="section-11" className="py-8" data-testid="privacy-section-11">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 11</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                                Contact Us
                            </h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                                If you have questions about this Privacy Policy or our privacy practices, please contact us:
                            </p>
                            <div className="mt-6 border border-border bg-card p-6">
                                <div className="font-display text-lg font-bold">SpLink.pro™</div>
                                <div className="mt-1 text-sm text-muted-foreground">Part of the SocialPulse Ecosystem</div>
                                <div className="mt-4 space-y-2 font-mono text-sm">
                                    <a href="mailto:support@splink.pro" className="flex items-center gap-2 text-primary hover:underline" data-testid="privacy-contact-email">
                                        <Mail className="h-4 w-4" /> support@splink.pro
                                    </a>
                                    <a href="https://splink.pro" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline" data-testid="privacy-contact-web">
                                        splink.pro
                                    </a>
                                </div>
                            </div>

                            <p className="mt-10 border-t border-border pt-8 text-sm italic text-muted-foreground">
                                By using SpLink.pro™, you acknowledge that you have read and understood this Privacy Policy.
                            </p>
                        </section>

                        <VersionHistory docKey="privacy" testId="privacy-version-history" />
                    </article>
                </div>
            </section>

            </LegalLanguageSwitcher>

            <footer className="mx-auto max-w-6xl px-6 py-12">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <Logo />
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground">About</Link>
                        <Link to="/terms" className="hover:text-foreground">Terms</Link>
                        <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
                        <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
                        <Link to="/dpa" className="hover:text-foreground">DPA</Link>
                        <span>© {new Date().getFullYear()} SpLink.pro™</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
